// auto completer para filtro de courier
import React, { useState, useEffect, useRef } from 'react'
import {
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Popper,
  Paper,
  ClickAwayListener
} from '@mui/material'
import { Search, Clear } from '@mui/icons-material'
import { ComponentProps } from '../../../../types/filters'
import { Courier } from '../../../../interfaces/Courier'
import { getCourierListCached } from './courierListCache'

interface CourierAutoCompleterProps extends ComponentProps {
  // Si viene en true, trae también los couriers inactivos (onlyActive=false)
  includeInactive?: boolean
}

export const CourierAutoCompleter: React.FC<CourierAutoCompleterProps> = ({
  value,
  onChange,
  fullWidth = true,
  includeInactive
}) => {
  const [searchText, setSearchText] = useState('')
  const [selected, setSelected] = useState<Courier | null>(null)
  const [allCouriers, setAllCouriers] = useState<Courier[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLoading(true)
    getCourierListCached(includeInactive ? false : undefined)
      .then((couriers) => setAllCouriers(couriers))
      .catch(() => setAllCouriers([]))
      .finally(() => setLoading(false))
  }, [includeInactive])

  useEffect(() => {
    if (!value && selected) {
      setSelected(null)
      setSearchText('')
    }
  }, [value])

  const trimmedSearch = searchText.trim()

  const filteredOptions = trimmedSearch.length >= 3
    ? allCouriers.filter((courier) =>
        courier.name.toLowerCase().includes(trimmedSearch.toLowerCase())
      )
    : []

  const handleSelect = (courier: Courier) => {
    setSelected(courier)
    setSearchText('')
    setIsOpen(false)

    onChange({
      target: { value: courier.name, name: 'courierName' }
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleClear = () => {
    setSelected(null)
    setSearchText('')
    onChange({ target: { value: '', name: 'courierName' } } as React.ChangeEvent<HTMLInputElement>)
  }

  const displayValue = selected ? selected.name : searchText

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div ref={anchorRef} style={{ position: 'relative', width: fullWidth ? '100%' : 300 }}>
        <TextField
          value={displayValue}
          onChange={(e) => { setSearchText(e.target.value); setIsOpen(true) }}
          onFocus={() => setIsOpen(true)}
          variant='outlined'
          size='small'
          placeholder='Buscar Courier...'
          autoComplete='off'
          fullWidth
          sx={{
            height: '44px',
            '& .MuiInputBase-root': { height: '44px' }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : selected ? (
                    <Clear style={{ cursor: 'pointer' }} onClick={handleClear} />
                  ) : (
                    <Search />
                  )}
                </InputAdornment>
              )
            }
          }}
        />

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          style={{ width: anchorRef.current?.offsetWidth || 'auto', zIndex: 1300 }}
        >
          <Paper elevation={3} style={{ maxHeight: 300, overflow: 'auto' }}>
            {trimmedSearch.length > 0 && trimmedSearch.length < 3 && (
              <MenuItem disabled>Escriba al menos 3 caracteres</MenuItem>
            )}
            {trimmedSearch.length >= 3 && !loading && filteredOptions.length === 0 && (
              <MenuItem disabled>No se encontraron resultados</MenuItem>
            )}
            {filteredOptions.map((courier) => (
              <MenuItem key={courier.courierId} onClick={() => handleSelect(courier)} style={{ cursor: 'pointer' }}>
                {courier.name}
              </MenuItem>
            ))}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}
